import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API, apiClient } from '../api_clients';
import { useOrganizationStore } from '../stores/organization-store';
import { useRBAC } from '../../hooks/use-rbac';
import type {
  ResponsesQBAuthURLData,
  ResponsesQBStatusData,
  HandleQBCallbackRequest,
  SeedDefaultsRequest,
  BulkSaveRequest,
  CreateMappingRequest,
  UpdateMappingRequest,
  ResponsesQBCachedAccountsListResponseData,
  ResponsesQBCachedAccountsFetchResponseData,
  ResponsesQBMappingsListResponseData,
  ResponsesQBMappingCategoryResp,
  ResponsesQBMappingsSeedResponseData,
  ResponsesQBMappingsBulkSaveResponseData,
  ResponsesQBMappingListItemResp,
  ResponsesQBMappingsValidateResponseData,
} from '../api_clients/api/api';

// ============================================================
// Types for the new 10-category, tag-based mapping system
// Re-export generated types for convenience
// ============================================================

// Re-export commonly used types from the generated API
export type {
  ResponsesQBCachedAccountResp as QBCachedAccount,
  ResponsesQBCachedAccountsListResponseData as QBCachedAccountsResponse,
  ResponsesQBMappingListItemResp as QBMappingTag,
  ResponsesQBMappingCategoryResp as QBMappingCategory,
  ResponsesQBMappingSummaryResp as QBMappingsSummary,
  ResponsesQBMappingsListResponseData as QBMappingsResponse,
  ResponsesQBMappingValidationIssue as QBValidationIssue,
  ResponsesQBMappingsValidateResponseData as QBValidationResponse,
  ResponsesQBMappingsBulkSaveResponseData as QBBulkSaveResponse,
  ResponsesQBMappingsSeedResponseData as QBSeedResponse,
  ResponsesQBCachedAccountsFetchResponseData as QBFetchAccountsResponse,
  BulkSaveRequest as QBBulkSaveRequest,
  SeedDefaultsRequest as QBSeedRequest,
  RequestsBulkMappingEntry as QBBulkMappingInput,
} from '../api_clients/api/api';

export type MappingType = 'expense' | 'revenue' | 'dual' | 'mixed';

// Query keys
export const quickbooksKeys = {
  all: ['quickbooks'] as const,
  status: (orgId: string) => [...quickbooksKeys.all, 'status', orgId] as const,
  cachedAccounts: (orgId: string) => [...quickbooksKeys.all, 'cachedAccounts', orgId] as const,
  mappings: (orgId: string) => [...quickbooksKeys.all, 'mappings', orgId] as const,
  mappingCategory: (orgId: string, category: string) => [...quickbooksKeys.all, 'mappings', orgId, category] as const,
  validation: (orgId: string, syncType: string) => [...quickbooksKeys.all, 'validation', orgId, syncType] as const,
};

export function useQuickbooksStatus() {
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: quickbooksKeys.status(orgUnid),
    queryFn: async (): Promise<ResponsesQBStatusData | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.quickbooksApi.getQBStatus(
        orgUnid,
        ""
      );
      return response.data?.data || null;
    },
    enabled: !!orgUnid && isAdminOrOwner,
  });
}

export function useGetQuickbooksAuthUrl() {
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (): Promise<ResponsesQBAuthURLData | null> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.quickbooksApi.getQBAuthURL(
        orgUnid,
        ""
      );
      return response.data?.data || null;
    },
  });
}

export function useDisconnectQuickbooks() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async () => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      const response = await apiClient.quickbooksApi.disconnectQB(orgUnid, "");
      return response.data?.data;
    },
    onSuccess: () => {
      // Invalidate status query to refresh the integration state
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.status(orgUnid) });
      }
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
    },
  });
}

export function useListQuickbooksAccounts() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async () => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.quickbooksApi.listAccounts(
        orgUnid,
        ""
      );
      return response.data?.data;
    },
    onSuccess: () => {
      // Invalidate status query to refresh the integration state
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.status(orgUnid) });
      }
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
    },
  });
}

export function useQuickbooksAccountMapping() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: API.MapAccountsRequest) => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.quickbooksApi.mapAccounts(
        orgUnid,
        "",
        data
      );
      return response.data?.data;
    },
    onSuccess: () => {
      // Invalidate status query to refresh the integration state
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.status(orgUnid) });
      }
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
    },
  });
}


export function useQuickbooksSyncCustomers() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: API.SyncCustomersRequest) => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.quickbooksApi.syncCustomers(
        orgUnid,
        "",
        data
      );
      return response.data?.data;
    },
    onSuccess: () => {
      // Invalidate status query to refresh the integration state
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.status(orgUnid) });
      }
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
    },
  });
}

export function useQuickbooksSyncPayments() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async () => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.quickbooksApi.syncPayments(
        orgUnid,
        "",
        {}
      );
      return response.data?.data;
    },
    onSuccess: () => {
      // Invalidate status query to refresh the integration state
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.status(orgUnid) });
      }
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
    },
  });
}


export function useQuickbooksCallback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { code: string; state: string; realmId: string }) => {
      const request: HandleQBCallbackRequest = {
        code: data.code,
        state: data.state,
        realmId: data.realmId,
      };
      const response = await apiClient.quickbooksCallbackApi.handleQBCallback(request);
      return response.data?.data;
    },
    onSuccess: () => {
      // Invalidate all quickbooks queries to refresh the integration state
      queryClient.invalidateQueries({ queryKey: quickbooksKeys.all });
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
    },
  });
}

// ============================================================
// New hooks for the 10-category, tag-based mapping system
// ============================================================

/**
 * Fetch QB Chart of Accounts into the local cache
 * Call this immediately after connecting QB
 */
export function useQuickbooksFetchAccounts() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (): Promise<ResponsesQBCachedAccountsFetchResponseData | null | undefined> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.qbCachedAccountsApi.fetchAccounts(
        orgUnid,
        '',
        {}
      );
      return response.data?.data;
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.cachedAccounts(orgUnid) });
      }
    },
  });
}

/**
 * Query cached QB accounts with optional filters
 */
export function useQuickbooksCachedAccounts(filters?: {
  search?: string;
  type?: string;
  active?: boolean;
}) {
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: [...quickbooksKeys.cachedAccounts(orgUnid), filters],
    queryFn: async (): Promise<ResponsesQBCachedAccountsListResponseData | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.qbCachedAccountsApi.listCachedAccounts(
        orgUnid,
        '',
        filters?.active,
        filters?.search,
        filters?.type
      );
      return response.data?.data || null;
    },
    enabled: !!orgUnid && isAdminOrOwner,
  });
}

/**
 * Seed default mappings for all predefined tags
 */
export function useQuickbooksSeedMappings() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: SeedDefaultsRequest = {}): Promise<ResponsesQBMappingsSeedResponseData | null | undefined> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.qbMappingsApi.seedDefaults(
        orgUnid,
        '',
        data
      );
      return response.data?.data;
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.mappings(orgUnid) });
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.status(orgUnid) });
      }
    },
  });
}

/**
 * Get all QB mappings grouped by category
 */
export function useQuickbooksMappings(options?: { mappedOnly?: boolean }) {
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: [...quickbooksKeys.mappings(orgUnid), options],
    queryFn: async (): Promise<ResponsesQBMappingsListResponseData | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.qbMappingsApi.listAllMappings(
        orgUnid,
        '',
        options?.mappedOnly
      );
      return response.data?.data || null;
    },
    enabled: !!orgUnid && isAdminOrOwner,
  });
}

/**
 * Get mappings for a single category
 */
export function useQuickbooksMappingCategory(category: string) {
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: quickbooksKeys.mappingCategory(orgUnid, category),
    queryFn: async (): Promise<ResponsesQBMappingCategoryResp | null> => {
      if (!orgUnid || !category) return null;
      const response = await apiClient.qbMappingsApi.listByCategory(
        category,
        orgUnid,
        ''
      );
      return response.data?.data || null;
    },
    enabled: !!orgUnid && !!category && isAdminOrOwner,
  });
}

/**
 * Bulk save mappings for a category
 */
export function useQuickbooksSaveMappingsBulk() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: BulkSaveRequest): Promise<ResponsesQBMappingsBulkSaveResponseData | null | undefined> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.qbMappingsApi.bulkSave(
        orgUnid,
        '',
        data
      );
      return response.data?.data;
    },
    onSuccess: (_, variables) => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.mappings(orgUnid) });
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.mappingCategory(orgUnid, variables.category) });
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.status(orgUnid) });
      }
    },
  });
}

/**
 * Update a single mapping by ID
 */
export function useQuickbooksUpdateMapping() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: {
      mappingId: string;
      qbExpenseAccountId?: string;
      qbExpenseAccountName?: string;
      qbRevenueAccountId?: string;
      qbRevenueAccountName?: string;
      isDefault?: boolean;
    }): Promise<ResponsesQBMappingListItemResp | null | undefined> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const { mappingId, ...body } = data;
      const request: UpdateMappingRequest = {
        qbExpenseAccountId: body.qbExpenseAccountId,
        qbExpenseAccountName: body.qbExpenseAccountName,
        qbRevenueAccountId: body.qbRevenueAccountId,
        qbRevenueAccountName: body.qbRevenueAccountName,
        isDefault: body.isDefault,
      };
      const response = await apiClient.qbMappingsApi.updateMapping(
        mappingId,
        orgUnid,
        '',
        request
      );
      return response.data?.data?.mapping;
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.mappings(orgUnid) });
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.status(orgUnid) });
      }
    },
  });
}

/**
 * Create a new custom mapping (for deduction category)
 */
export function useQuickbooksCreateMapping() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: {
      category: string;
      tag: string;
      qbExpenseAccountId?: string;
      qbExpenseAccountName?: string;
      qbRevenueAccountId?: string;
      qbRevenueAccountName?: string;
      isDefault?: boolean;
    }): Promise<ResponsesQBMappingListItemResp | null | undefined> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const request: CreateMappingRequest = {
        category: data.category,
        tag: data.tag,
        qbExpenseAccountId: data.qbExpenseAccountId,
        qbExpenseAccountName: data.qbExpenseAccountName,
        qbRevenueAccountId: data.qbRevenueAccountId,
        qbRevenueAccountName: data.qbRevenueAccountName,
        isDefault: data.isDefault,
      };
      const response = await apiClient.qbMappingsApi.createMapping(
        orgUnid,
        '',
        request
      );
      return response.data?.data?.mapping;
    },
    onSuccess: (_, variables) => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.mappings(orgUnid) });
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.mappingCategory(orgUnid, variables.category) });
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.status(orgUnid) });
      }
    },
  });
}

/**
 * Delete or clear a mapping
 */
export function useQuickbooksDeleteMapping() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: { mappingId: string; hard?: boolean }): Promise<void> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      await apiClient.qbMappingsApi.deleteMapping(
        data.mappingId,
        orgUnid,
        '',
        data.hard
      );
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.mappings(orgUnid) });
        queryClient.invalidateQueries({ queryKey: quickbooksKeys.status(orgUnid) });
      }
    },
  });
}

/**
 * Validate mappings before syncing
 */
export function useQuickbooksValidateMappings(syncType: 'invoice' | 'settlement' | 'all' = 'all') {
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: quickbooksKeys.validation(orgUnid, syncType),
    queryFn: async (): Promise<ResponsesQBMappingsValidateResponseData | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.qbMappingsApi.validate(
        orgUnid,
        syncType,
        ''
      );
      return response.data?.data || null;
    },
    enabled: !!orgUnid && isAdminOrOwner,
  });
}
